import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Pause, Play, RotateCcw, Maximize, Timer, Volume2, VolumeX, MonitorUp, PlayCircle, Repeat, Link2, Check } from 'lucide-react';
import { SCENES, PRESENTATION, TOTAL_SECONDS } from '../presentationConfig';
import { useClock } from '../engine/useClock';
import { Stage, fmtClock } from '../engine/Stage';
import { usePreload } from '../engine/Preloader';
import { usePresentationKeys, toggleFullscreen, scenePoster } from '../engine/controls';
import { MotionConfig } from 'framer-motion';

export default function PreviewMode() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const startScene = Math.max(0, (parseInt(params.get('scene') || '1', 10) || 1) - 1);
  const [autoAdvance, setAutoAdvance] = useState(params.get('autoplay') === 'true');
  const [showTimer, setShowTimer] = useState(true);
  const [audioOn, setAudioOn] = useState(PRESENTATION.audio.defaultOn);
  const stageRef = useRef(null);
  const preload = usePreload(true);
  const clock = useClock({ scenes: SCENES, duration: PRESENTATION.sceneDuration, initialIndex: startScene, autoplay: false, autoAdvance, speed: Number(params.get('speed')) || 1 });
  const started = useRef(false);
  const startPlay = clock.play;
  // hold the first scene until assets are in memory (max 25s) so preview never buffers
  useEffect(() => {
    if (started.current) return undefined;
    if (preload.ready) { started.current = true; startPlay(); return undefined; }
    const id = setTimeout(() => { if (!started.current) { started.current = true; startPlay(); } }, 25000);
    return () => clearTimeout(id);
  }, [preload.ready, startPlay]);

  const keys = useMemo(() => ({
    next: clock.next, prev: clock.prev, toggle: clock.toggle, restart: clock.restart,
    fullscreen: () => toggleFullscreen(stageRef.current), timer: () => setShowTimer((v) => !v), audio: () => setAudioOn((v) => !v),
  }), [clock.next, clock.prev, clock.toggle, clock.restart]);
  usePresentationKeys(keys, true);

  const jump = useCallback((i) => { clock.goTo(i); clock.play(); }, [clock]);
  const scene = SCENES[clock.index];

  // Approval link: passcode-gated viewer that just plays the deck — no notes, script or control centre.
  const [copied, setCopied] = useState(false);
  const shareLink = `${window.location.origin}/jackson-stops-presentation/watch`;
  const copyShare = useCallback(async () => {
    try { await navigator.clipboard.writeText(shareLink); } catch { window.prompt('Copy this link', shareLink); }
    setCopied(true); setTimeout(() => setCopied(false), 3500);
  }, [shareLink]);

  return (
    <MotionConfig reducedMotion="user">
      <div className="pk-preview" data-testid="pk-preview-mode">
        <header className="pk-preview-head">
          <div className="pk-preview-brand">
            <img src="/nichecom-logo-nav.png" alt="NicheCom" />
            <div>
              <div className="pk-preview-title">{PRESENTATION.title}</div>
              <div className="pk-preview-sub">{PRESENTATION.subtitle} · {SCENES.length} × {PRESENTATION.sceneDuration}s · {fmtClock(TOTAL_SECONDS)}</div>
            </div>
          </div>
          <div className="pk-preview-actions">
            <span className={`pk-pill ${preload.ready ? 'ok' : ''}`} data-testid="pk-preload-pill">{preload.ready ? 'Assets ready' : `Preparing assets · ${preload.pct}%`}</span>
            <button className={`pk-btn pk-btn-ghost ${autoAdvance ? 'on' : ''}`} onClick={() => setAutoAdvance((v) => !v)} data-testid="pk-autoplay-toggle"><Repeat size={16} /> Auto-advance {autoAdvance ? 'on' : 'off'}</button>
            <button className={`pk-btn pk-btn-ghost ${showTimer ? 'on' : ''}`} onClick={() => setShowTimer((v) => !v)} data-testid="pk-timer-toggle"><Timer size={16} /> Timer</button>
            <button className={`pk-btn pk-btn-ghost ${audioOn ? 'on' : ''}`} onClick={() => setAudioOn((v) => !v)} data-testid="pk-audio-toggle">{audioOn ? <Volume2 size={16} /> : <VolumeX size={16} />} Audio</button>
            <button className="pk-btn pk-btn-ghost" onClick={() => window.open('/jackson-stops-presentation/presenter', 'pk-presenter', 'width=900,height=600')} data-testid="pk-presenter-window"><MonitorUp size={16} /> Presenter window</button>
            <button className={`pk-btn pk-btn-ghost ${copied ? 'on' : ''}`} onClick={copyShare} title={`${shareLink} · viewer passcode: ${PRESENTATION.viewerPasscode}`} data-testid="pk-share-link">{copied ? <Check size={16} /> : <Link2 size={16} />} {copied ? `Link copied · passcode ${PRESENTATION.viewerPasscode}` : 'Share link'}</button>
            <button className="pk-btn pk-btn-ghost" onClick={() => toggleFullscreen(stageRef.current)} data-testid="pk-preview-fullscreen"><Maximize size={16} /> Fullscreen</button>
            <a className="pk-btn pk-btn-lime" href="/jackson-stops-presentation/present?autoplay=true" data-testid="pk-play-presentation"><PlayCircle size={18} /> Play presentation</a>
          </div>
        </header>

        <main className="pk-preview-main">
          <div className="pk-preview-stage" ref={stageRef}>
            <Stage scenes={SCENES} clock={clock} showTimer={showTimer} mode="preview" audioOn={audioOn} />
            {!started.current && !preload.ready && (
              <div className="pk-stage-preparing" data-testid="pk-stage-preparing">
                <div className="pk-ready-bar"><div className="pk-ready-fill" style={{ width: `${preload.pct}%` }} /></div>
                <span>Preparing assets · {preload.pct}%</span>
              </div>
            )}
          </div>
          <div className="pk-transport" data-testid="pk-transport">
            <div className="pk-transport-now"><b>{String(clock.index + 1).padStart(2, '0')}</b> {scene.title}</div>
            <div className="pk-transport-buttons">
              <button onClick={clock.prev} title="Previous (←)" data-testid="pk-preview-prev"><ChevronLeft size={20} /></button>
              <button onClick={clock.toggle} className="pk-ctrl-main" title="Pause / resume (space)" data-testid="pk-preview-toggle">{clock.playing ? <Pause size={20} /> : <Play size={20} />}</button>
              <button onClick={clock.replayScene} title="Replay scene" data-testid="pk-preview-replay"><RotateCcw size={18} /></button>
              <button onClick={clock.next} title="Next (→)" data-testid="pk-preview-next"><ChevronRight size={20} /></button>
            </div>
            <div className="pk-transport-time" data-testid="pk-preview-time">
              <span>{fmtClock(clock.t)} / {fmtClock(PRESENTATION.sceneDuration)}</span>
              <small>{fmtClock(clock.overall)} / {fmtClock(TOTAL_SECONDS)}</small>
            </div>
          </div>
        </main>

        <nav className="pk-filmstrip" data-testid="pk-filmstrip">
          {SCENES.map((s, i) => {
            const poster = scenePoster(s);
            return (
              <button key={s.id} className={`pk-film ${i === clock.index ? 'active' : ''} ${i < clock.index ? 'done' : ''}`} onClick={() => jump(i)} data-testid={`pk-film-${s.n}`}>
                <div className="pk-film-thumb">{poster ? <img src={poster} alt="" loading="lazy" /> : <div className="pk-film-blank" />}<span>{String(s.n).padStart(2, '0')}</span></div>
                <div className="pk-film-title">{s.title}</div>
              </button>
            );
          })}
        </nav>
        <footer className="pk-preview-foot">PechaKucha format · 20 scenes × 20 seconds · 6:40 · Keyboard: ← → navigate · Space pause · F fullscreen</footer>
      </div>
    </MotionConfig>
  );
}
